// Shared reCAPTCHA v3 lazy-injection helpers for embed bundles.
//
// Extracted from FormsEmbed (TD-221 Phase 4) so both forms and
// marketplace embeds share one implementation. Follows project
// rule #20: script injected ONLY on user action, never in index.html.

let recaptchaLoading: Promise<void> | null = null;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Locally-scoped view of the grecaptcha global exposing render() and the
// widget-id form of execute() — used ONLY by the explicit-render coexist
// fallback below. Deliberately NOT added to the `declare global` block
// above: that interface is merged program-wide with portal/lib/recaptcha.ts's
// own (narrower, intentionally out-of-scope) Window.grecaptcha declaration,
// and TS requires every declaration of a merged global interface to agree
// byte-for-byte (TS2717) — widening it here broke typecheck for the
// unrelated portal file. Cast at the one call site instead.
interface GrecaptchaExplicit {
  ready: (cb: () => void) => void;
  // execute() accepts either a site key (the "implicit"/render=<key> URL
  // form, above) or a numeric widget id returned by render() (the
  // "explicit" form). Both accept the same {action} options object.
  execute: (siteKeyOrWidgetId: string | number, options?: { action: string }) => Promise<string>;
  // Explicit rendering — registers a SECOND v3 site key on a page that
  // already auto-registered a different key via the render=<key> URL param
  // (measured; see acquireViaExplicitRender). size:"invisible" keeps it
  // badge-only, matching the implicit-render UX.
  render: (container: Element, params: { sitekey: string; size: "invisible" }) => number;
}

function grecaptchaExplicit(): GrecaptchaExplicit {
  return window.grecaptcha as unknown as GrecaptchaExplicit;
}

/**
 * Lazily inject the reCAPTCHA v3 script tag. Idempotent — dedupes if
 * the script is already loaded or loading. Returns immediately (no
 * script load) when siteKey is empty/undefined, so keyless dev works.
 */
export function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (!siteKey) return Promise.resolve();
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (recaptchaLoading) return recaptchaLoading;

  recaptchaLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("reCAPTCHA script failed to load")), { once: true });
    document.head.appendChild(script);
  });

  return recaptchaLoading;
}

// 2026-09-29 production hotfix: a third-party script on the host page
// (a chat widget, separate project) can call the public reCAPTCHA API
// with ITS OWN site key before our embed runs. loadRecaptchaScript then
// short-circuits (window.grecaptcha already present) and our key is
// never registered with the API — grecaptcha.execute(ourKey) throws
// synchronously *inside* the grecaptcha.ready() callback ("Invalid site
// key or not loaded in api.js"). grecaptcha.ready() can invoke that
// callback asynchronously, outside this function's own call stack, so
// an uncaught throw inside it does NOT reject the surrounding
// `new Promise` executor — it becomes an unhandled exception and the
// promise never settles. Every caller's `await executeRecaptchaToken(...)`
// then hangs forever (submit buttons stuck on "Sending…").
//
// Two independent fixes below: (a) catch a synchronous throw from
// execute() (and ready()) and turn it into an explicit rejection; (b)
// bound the *whole* acquisition — script load, ready, execute — with a
// timeout, so a caller's await never hangs even for a failure mode
// nobody has anticipated yet (e.g. api.js loads but never calls back).
//
// 8s budget: script load + ready + execute measures well under 2s on a
// healthy connection (execute() is near-instant once ready() fires);
// 8s gives generous headroom for a slow network without leaving a user
// staring at a frozen submit control for an unbounded time. Exported so
// tests assert against the same constant (rule 55) rather than a
// duplicated literal.
export const RECAPTCHA_ACQUIRE_TIMEOUT_MS = 8000;

/** Race `promise` against `timeoutMs`; rejects with `message` if the
 * timer wins. Always clears its own timer on settlement either way, so
 * a resolved/rejected `promise` never leaves a dangling timer. */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** Run `fn` inside a Promise executor, catching a SYNCHRONOUS throw (e.g.
 * grecaptcha.execute() rejecting the ready() callback outright) and turning
 * it into a rejection instead of an unhandled exception. */
function toRejectingPromise<T>(fn: (resolve: (v: T) => void, reject: (e: unknown) => void) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    try {
      fn(resolve, reject);
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/** "Implicit" path: siteKey was registered via the render=<key> URL param
 * (either by loadRecaptchaScript itself, or — the normal, unbroken case —
 * because no other script had touched window.grecaptcha yet). */
function acquireViaDirectExecute(siteKey: string, action: string): Promise<string> {
  return toRejectingPromise<string>((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      toRejectingPromise<string>((res, rej) => {
        window.grecaptcha!.execute(siteKey, { action }).then(res, rej);
      }).then(resolve, reject);
    });
  });
}

// 2026-09-29 coexist fix: one explicit-render widget id per site key,
// memoized so a form submitted twice in one session doesn't grow a new
// hidden <div> (and Google widget) on every submit.
const coexistWidgetIds = new Map<string, number>();

function getCoexistWidgetId(siteKey: string): number {
  const cached = coexistWidgetIds.get(siteKey);
  if (cached !== undefined) return cached;
  const container = document.createElement("div");
  container.style.display = "none";
  document.body.appendChild(container);
  const widgetId = grecaptchaExplicit().render(container, { sitekey: siteKey, size: "invisible" });
  coexistWidgetIds.set(siteKey, widgetId);
  return widgetId;
}

/** "Explicit" fallback path — see the GrecaptchaExplicit interface above.
 * Measured in real Chromium (2026-09-29, against the live host page with the
 * other script present): grecaptcha.render() registers a SECOND v3 site key
 * alongside one already loaded via the render=<key> URL param, in either
 * load order, without disturbing the first key's own execute() calls; a
 * second api.js script tag for our key does NOT register it (same "Invalid
 * site key" error). */
function acquireViaExplicitRender(siteKey: string, action: string): Promise<string> {
  return toRejectingPromise<string>((resolve, reject) => {
    grecaptchaExplicit().ready(() => {
      toRejectingPromise<string>((res, rej) => {
        const widgetId = getCoexistWidgetId(siteKey);
        grecaptchaExplicit()
          .execute(widgetId, { action })
          .then(res, rej);
      }).then(resolve, reject);
    });
  });
}

/**
 * Execute reCAPTCHA v3 and return the token string. Lazy-loads the
 * script first (idempotent). Returns "" when siteKey is empty so
 * keyless dev paths work without branching in the caller.
 *
 * Never hangs: the entire acquisition (script load + ready + execute,
 * including the coexist fallback below) is bounded by
 * RECAPTCHA_ACQUIRE_TIMEOUT_MS, and a synchronous throw from
 * grecaptcha.ready()/execute() is caught and turned into a rejection
 * instead of an unhandled exception (see the 2026-09-29 hotfix comment
 * above loadRecaptchaScript). Callers should already treat any rejection
 * as "security check failed" — this only makes sure that rejection
 * always arrives.
 *
 * Coexists with another script's reCAPTCHA key: if window.grecaptcha was
 * already registered for a DIFFERENT site key before this call (e.g. a
 * chat widget elsewhere on the host page loaded first), the direct
 * execute(siteKey) call fails with "Invalid site key or not loaded in
 * api.js" — this function then falls back to explicit rendering
 * (acquireViaExplicitRender) to register OUR key alongside the existing
 * one, rather than giving up. If that fallback also fails, the ORIGINAL
 * direct-execute error is what surfaces (it is the more informative one
 * on the common, non-coexisting path).
 */
export async function executeRecaptchaToken(siteKey: string, action: string): Promise<string> {
  if (!siteKey) return "";

  const acquire = (async (): Promise<string> => {
    await loadRecaptchaScript(siteKey);
    if (!window.grecaptcha) return "";
    try {
      return await acquireViaDirectExecute(siteKey, action);
    } catch (directErr) {
      try {
        return await acquireViaExplicitRender(siteKey, action);
      } catch {
        throw directErr;
      }
    }
  })();

  return withTimeout(acquire, RECAPTCHA_ACQUIRE_TIMEOUT_MS, "reCAPTCHA token acquisition timed out");
}

/** Test-only: reset the memoized reCAPTCHA load promise and any explicit-
 * render coexist widget ids (rule 55 — real state, not a fixture double). */
export function resetRecaptchaForTests(): void {
  recaptchaLoading = null;
  coexistWidgetIds.clear();
}
